import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Column, SortConfig } from '../types';
import { TableSkeleton } from './Skeleton';
import { useFlipReorder } from '../utils/useFlipReorder'; // [FLIP-REORDER 2026-06-26]
import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import MobileRecordList, { MobileCardConfig } from './MobileRecordList'; // [MOBILE 2026-07-07]
import { useColumnResize, ResizeHandle, CutCell, CutLabel, cutCellStyle } from './ColumnResize'; // [COL-RESIZE 2026-09-29]
import { LoadFailed, EmptyState, EmptyStateProps } from './LoadState'; // [LOAD-STATES 2026-09-29]
import { useSearchHighlight } from '../hooks/useSearchHighlight'; // [SEARCH-HIGHLIGHT 2026-09-29]
import { useRowsLand } from './Fluid'; // [FLUID-4 2026-10-12] [FLUID-5 2026-10-12]

/* ────────────────────────────────────────────────────────────────────────────
   Row hover that can never "stick" AND can never appear on a row the cursor
   isn't on. Read this before touching the hover code.

   THE TRAP (do not reintroduce it): two earlier approaches both failed the same
   way. (1) CSS `:hover` — browsers only re-evaluate `:hover` on the next *real*
   pointer event, so when rows shift under a stationary cursor (a silent reload
   reorders/inserts/removes rows, a reflow nudges the table, the window resizes)
   the tint stuck on the row that WAS under the cursor → "random stuck dark row".
   (2) Tracking the cursor and REPLAYING a stored coordinate
   (`document.elementFromPoint(lastPoint)`) on scroll/resize/reload to re-pick a
   row. That fixed sticking but introduced the opposite bug: every silent
   background reload (60s poll / 5-min refresh / realtime DB push on the Payments
   page) replayed the stored point and PAINTED a grey row on its own — and the
   "clear on leave" guards couldn't save it, because Chromium often doesn't fire
   a leave event when the cursor exits via the viewport edge, so the stored point
   stayed live and got replayed. Replaying a stored coordinate on a non-pointer
   event is the root cause of BOTH bugs. Never do it.

   THE MODEL HERE — asymmetric, and that asymmetry is the whole fix:
     • The tint is SET only by a live pointer event whose target is a row of this
       table (onPointerMove → onRowPointer). The row comes from the event's own
       target, so it is by definition the row physically under the cursor at that
       instant — never a stored/replayed position.
     • EVERY "content shifted" trigger — scroll, wheel, table resize, data
       reload, pointer leaving the table, window blur, tab hidden — only CLEARS
       the tint. None of them can ever set it.
   Consequence (the guarantee): a background data refresh can only ever ERASE the
   highlight, never paint one, so a phantom grey row is unreachable — and it no
   longer depends on any leave event firing. The single cost: if a silent reload
   lands while the cursor is holding perfectly still on a row, the tint clears
   and reappears on the next pixel of movement. This is the same proven behaviour
   the rest of the app's tables use (see utils/hoverGate.ts). `hover: hover` keeps
   touch / hybrid devices from ever painting a tint.
   ──────────────────────────────────────────────────────────────────────────── */

// [MOTION-A 2026-08-20] The table fades in when it replaces the skeleton (DataTable
// early-returns the skeleton while `loading`, so this wrapper mounts fresh at the handoff
// and its mount animation is exactly the skeleton→data transition).
//
// ⚠ OPACITY ONLY — deliberately no translate. This element is the table's scroll container
// and the sticky <thead> pins to it ([STICKY-HEAD] below); a transform on an ancestor
// creates a containing block and can break `position: sticky`. Opacity is composited and
// carries no such risk.
const TableWrapper = styled.div<{ $parentScroll?: boolean }>`
  animation: dtReveal 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  @keyframes dtReveal {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  /* [FLUID-4/5 2026-10-12, user] …and every row on screen rises in, top to bottom, as the data lands (the ROWS move,
     never this wrapper — see the sticky note above): DataTable's useRowsLand, components/Fluid.tsx landRows. */

  /* [STICKY-HEAD 2026-06-26] $parentScroll: do NOT be a scroll container — let an outer
     wrapper own the scroll so a page can freeze a block ABOVE the table and have the
     sticky <thead> pin to that outer scroller (e.g. Payments). Default: own the x-scroll
     with touch polish (momentum + contain the horizontal swipe so it scrolls the table
     instead of triggering the browser's back/forward gesture — no-ops on desktop). */
  ${(p) => (p.$parentScroll
    ? 'overflow: visible;'
    : 'overflow-x: auto; -webkit-overflow-scrolling: touch; overscroll-behavior-x: contain;')}
`;

const Table = styled.table<{ $fit?: boolean; $fitMin?: number }>`
  /* Default: grow to fit content and let the wrapper scroll horizontally when
     the table is wider than its container (used by wide tables like Disputes).
     With $fit: stay at 100% of the container so the full table is always
     visible without a horizontal scrollbar — long cells wrap instead. */
  /* [RESPONSIVE 2026-06-23] $fit gets a min-width floor so that below it the
     wrapper (overflow-x:auto) scrolls instead of squeezing the fit table's columns
     into tall many-line rows on narrow screens. Only the Payments table uses $fit. */
  /* [EDIT-COLUMNS 2026-09-28] $fitMin raises that floor when a page adds columns (the Payments
     admin extra columns): wider than the floor the table still fits with no scroll; narrower, the
     wrapper scrolls sideways. Unset = the old 860px, so no other table changes. */
  ${(p) => (p.$fit ? 'width: 100%;\n  min-width: ' + (p.$fitMin ?? 860) + 'px;' : 'min-width: 100%;\n  width: max-content;')}
  border-collapse: collapse;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Th = styled.th<{ $sortable?: boolean; $align?: string; $fit?: boolean; $nowrap?: boolean; $resizable?: boolean }>`
  background: ${(p) => p.theme.pill};
  padding: 10px 12px;
  text-align: ${(p) => p.$align || 'left'};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  /* Dark mode's green600 (#1a7f64) is too dim on the slate pill; use a
     brighter mint there so the headers read clearly. [PURE-INK 2026-10-15] A page under components/PureInk sets
     theme.tableHeadInk (the light theme's greenInk: 4.7 : 1 on the pill, green600 is 4.0 : 1); every other page is unchanged. */
  color: ${(p) => p.theme.tableHeadInk ?? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  /* Soft downward-only lift so rows scroll cleanly *under* the sticky header
     instead of blending into it. Negative spread keeps it from bleeding sideways. */
  box-shadow: 0 6px 10px -6px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(22,25,23,0.12)')};
  cursor: ${(p) => (p.$sortable ? 'pointer' : 'default')};
  /* With $fit, allow multi-word headers to wrap so columns can shrink and the
     whole table fits its container (they only wrap when actually squeezed).
     $nowrap opts a specific header out so it always stays on one line. */
  white-space: ${(p) => (p.$nowrap ? 'nowrap' : p.$fit ? 'normal' : 'nowrap')};
  user-select: none;
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);
  /* [COL-RESIZE 2026-09-29] anchor for the drag handle on the right edge */
  ${(p) => (p.$resizable ? 'position: relative;' : '')}

  &:hover {
    color: ${(p) =>
      p.theme.colorScheme === 'dark'
        ? '#7de5ab'
        : p.$sortable
        ? p.theme.green700
        : p.theme.green600};
  }

  .sort-icon {
    flex-shrink: 0;
    margin-left: 6px;
    font-size: 10px;
    opacity: 0.5;
  }

  .sort-icon.active {
    opacity: 1;
    color: ${(p) => p.theme.tableHeadInk ?? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
    font-size: 11px;
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: the shared phone cell rhythm, 8px 10px (the guide), so a wide table is a little
     narrower to swipe. Column resizing is off on phones, so CELL_PAD_X below is not used there. Desktop unchanged. */
  @media (max-width: 768px) {
    padding: 8px 10px;
  }
`;

// Keeps the sort arrow on the same line as the (possibly wrapping) header
// label instead of dropping it onto its own line. The label wraps inside the
// flex row; the icon stays vertically centred beside it.
const HeaderInner = styled.span<{ $align?: string }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) =>
    p.$align === 'right' ? 'flex-end' : p.$align === 'center' ? 'center' : 'flex-start'};
`;

/* [MOTION-B 2026-08-20] Two fixed keyframe sets rather than one built from the theme inside
   the styled template: a nested template literal would terminate the outer styled`` string
   (the same backtick trap as a backtick in a styled CSS comment). Emerald, matching the
   app's "saved / mapped" green. */
const rowFlashDark = keyframes`
  0%   { background: rgba(74, 205, 139, 0.26); }
  35%  { background: rgba(74, 205, 139, 0.20); }
  100% { background: transparent; }
`;
const rowFlashLight = keyframes`
  0%   { background: rgba(19, 183, 144, 0.20); }
  35%  { background: rgba(19, 183, 144, 0.15); }
  100% { background: transparent; }
`;

// [MOTION-B 2026-08-20] `$flash` = "this row is what you just saved". A soft tint blooms on
// the row and fades out over ~1.1s, so the eye lands on the changed row instead of scanning
// for it. Painted on the CELLS (like the hover tint) so it layers over the row's own status
// colour rather than replacing it, and it is a background animation on a handful of rows -
// never a transform, and never applied to a whole table at once.
const Tr = styled.tr<{ $bg?: string; $hover?: boolean; $flash?: boolean }>`
  background: ${(p) => p.$bg || 'transparent'};
  /* [WRITE-LATENCY 2026-08-25] The row's own status colour (rowBgFn: red unmapped → green
     mapped) used to snap. A 320ms colour transition lets a just-mapped row turn green
     while the FLIP glide (useFlipReorder, also 320ms) carries it into the mapped section,
     so the two read as one movement. Colour only - never layout, never a transform. */
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1);

  & > td {
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Row-hover identifier: a gentle translucent tint painted on the cells so it
     overlays each row's own status colour (rowBgFn) instead of replacing it.
     Driven by the JS hover tracker (the $hover prop), never the CSS :hover
     pseudo — see the file header for why. Guarded by hover:hover so touch /
     hybrid devices never get a tint. */
  @media (hover: hover) {
    & > td {
      background: ${(p) =>
        p.$hover
          ? p.theme.colorScheme === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(22,25,23,0.04)'
          : 'transparent'};
    }
  }

  /* The saved-row bloom. Declared AFTER the hover rule so it wins while it runs, and it
     ends on transparent, so the row hands straight back to the normal hover/status
     painting with nothing left behind. */
  ${(p) => (p.$flash ? css`
    & > td {
      animation: ${p.theme.colorScheme === 'dark' ? rowFlashDark : rowFlashLight}
        1100ms cubic-bezier(0.4, 0, 0.2, 1) both;
    }
    @media (prefers-reduced-motion: reduce) {
      & > td { animation: none; }
    }
  ` : '')}
`;

const Td = styled.td<{ $align?: string; $breakable?: boolean; $fit?: boolean }>`
  padding: 8px 12px;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: ${(p) => p.$align || 'left'};
  transition: border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  /* breakable: let long unbreakable tokens (emails, URLs) wrap so the column
     can shrink. Otherwise, in fit mode keep values (dates, amounts) on one
     line — the headers wrap to make room, not the data. */
  ${(p) => (p.$breakable
    ? 'overflow-wrap: anywhere; word-break: break-word;'
    : p.$fit ? 'white-space: nowrap;' : '')}

  @media (max-width: 600px) {
    padding: 8px 12px;
    font-size: 13px;
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: 8px 10px, the same as the header above. Desktop unchanged. */
  @media (max-width: 768px) {
    padding: 8px 10px;
  }
`;

// [COL-RESIZE 2026-09-29, user] Drag-to-resize lives in ./ColumnResize (shared by every table).
// Both Th and Td pad 12px left + 12px right here; the clamp boxes subtract that.
const CELL_PAD_X = 24;

interface RowProps {
  row: any;
  columns: Column[];
  bg?: string;
  hovered: boolean;
  fit?: boolean;
  rowIndex: number;
  // [MOTION-B 2026-08-20] Part of the memo comparison, so turning the flash on and off
  // repaints ONLY the row it belongs to.
  flash?: boolean;
  // [COL-RESIZE 2026-09-29] column key -> its width variable, for the columns someone dragged.
  // Changes only when the SET of dragged columns changes (never mid-drag), so the memo still holds.
  cut?: Record<string, string>;
}

// One table row, memoised so a hover change only repaints the row that gains
// and the row that loses the tint — every other row bails out of re-rendering
// (and so skips re-running its cells' `col.render`). For the bail-out to hold,
// callers must pass stable `columns` and `rowBgFn` references (PaymentsPage does
// via useMemo/useCallback) so `bg` stays referentially equal between renders.
const Row = React.memo<RowProps>(({ row, columns, bg, hovered, fit, rowIndex, flash, cut }) => (
  // data-flip-key = a STABLE row id so useFlipReorder can track this row across reorders.
  <Tr $bg={bg} $hover={hovered} $flash={flash} data-row-index={rowIndex} data-flip-key={row?.id != null ? String(row.id) : undefined}>
    {columns.map((col) => {
      const content = col.render ? col.render(row[col.key], row) : row[col.key] ?? '—';
      // [COL-RESIZE 2026-09-29] A dragged column's cell content sits in the clamp box (see CutCell).
      const v = cut?.[col.key];
      return (
        <Td key={col.key} $align={col.align} $fit={fit} $breakable={fit && col.breakable}>
          {v ? <CutCell data-dt-cut="" style={cutCellStyle(v, CELL_PAD_X, col.align)}>{content}</CutCell> : content}
        </Td>
      );
    })}
  </Tr>
));
Row.displayName = 'DataTableRow';

interface DataTableProps {
  columns: Column[];
  data: any[];
  sort: SortConfig;
  onSort: (column: string) => void;
  loading?: boolean;
  rowBgFn?: (row: any) => string | undefined;
  // [MOTION-B 2026-08-20] Return true for the row that was just saved to bloom it briefly.
  // Optional: a page that does not pass it behaves exactly as before.
  rowFlashFn?: (row: any) => boolean;
  // When true, the table stays at 100% of its container (no horizontal scroll);
  // columns flagged `breakable` wrap their content to make everything fit.
  fitWidth?: boolean;
  // [EDIT-COLUMNS 2026-09-28] Optional min-width floor (px) for fitWidth mode; default 860.
  fitMinWidth?: number;
  // [STICKY-HEAD 2026-06-26] When true, the table does NOT create its own scroll
  // container — an outer wrapper scrolls instead, so the sticky <thead> pins to that
  // outer scroller (lets a page freeze a block above the table). Payments uses this.
  parentScroll?: boolean;
  // [MOBILE 2026-07-07] Optional descriptor for what leads the phone card face
  // (title / value / meta / status). Ignored on desktop. Without it a heuristic
  // still produces usable cards. See MobileRecordList.
  mobileCard?: MobileCardConfig;
  // [MOBILE 2026-07-07] Opt OUT of the phone card view for tables that are really
  // numeric matrices / reconciliation grids (e.g. Stats), where cross-column
  // comparison matters and a card per row reads as meaningless. Such pages keep
  // the real table on mobile (it scrolls horizontally inside its own wrapper),
  // matching the desktop layout.
  disableMobileCards?: boolean;
  // [COL-RESIZE 2026-09-29, user] Turns on Excel-style drag-to-resize for this table and names the
  // localStorage entry the widths are remembered under (include the user id, so each person on a
  // shared browser keeps their own). Absent = no handles, automatic widths (every other table).
  resizeStorageKey?: string;
  // [LOAD-STATES 2026-09-29, user] The page's load FAILED: show the "Couldn't load …" countdown card
  // (it retries by itself) instead of rows, so a failure never reads as "no data".
  loadFailed?: { what: string; onRetry: () => unknown };
  // [LOAD-STATES 2026-09-29, user] What to say when there are no rows ("No refunds in September 2026",
  // "No payments match …" + Clear search). Absent = "No records found".
  empty?: EmptyStateProps;
  // [SEARCH-HIGHLIGHT 2026-09-29, user] The page's search text: matching letters in the rows get the
  // amber highlight. Absent / '' = no highlight.
  highlight?: string;
}

const DataTable: React.FC<DataTableProps> = ({ columns, data, sort, onSort, loading, rowBgFn, rowFlashFn, fitWidth, fitMinWidth, parentScroll, mobileCard, disableMobileCards, resizeStorageKey, loadFailed, empty, highlight }) => {
  // [MOBILE 2026-07-07] Drives the desktop-table vs mobile-card branch; matrix/report tables
  // (disableMobileCards) keep the real table on phones. Declared first (unconditionally) because
  // the column-resize hook below needs it.
  const isMobile = useIsMobile();
  const useMobileCards = isMobile && !disableMobileCards;
  // [COL-RESIZE 2026-09-29, user] Drag-to-resize, shared with every table (./ColumnResize). Off
  // unless the page passes resizeStorageKey; off on phones.
  const resize = useColumnResize({
    storageKey: resizeStorageKey,
    enabled: !isMobile,
    columnKeys: columns.map((c) => c.key),
    headerPadX: CELL_PAD_X,
    refresh: data,
  });
  const roRef = useRef<ResizeObserver | null>(null);
  // Index of the row currently under the pointer (null = none). Setting it to
  // the value it already holds is a no-op for React, so resting on a row fires
  // no extra renders; only crossing a row boundary does. See the file header:
  // this is SET only by live pointer events and CLEARED by everything else.
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // [FLIP-REORDER 2026-06-26] Glide rows into place when they re-sort (e.g. a row that
  // moves after an inline edit) instead of jumping. Keyed off `data` so it runs only on
  // an actual data/order change, never on a hover re-render.
  const flipBodyRef = useFlipReorder<HTMLTableSectionElement>([data]);

  // [FLUID-5 2026-10-12, user] "the animation should show for the complete table from top to bottom": when the rows
  // land — this table mounting with data (a tab visit), or data replacing the skeleton / an empty state — every row on
  // screen rises in as one wave. Not on a silent refresh, a sort or an edit (the key does not change then).
  const landRef = useRef<HTMLDivElement>(null);
  // [FLUID-6 2026-10-12] …and when a NEW set of rows swaps in WITHOUT a skeleton — a month / filter / page change answered
  // at once from the app's cache: more than half of the rows on show are new (by row id). A silent refresh, a sort or an
  // edit keeps the same rows, so it never waves; rows without an id are never counted as new. Decided during render and
  // idempotent (the same data gives the same answer), so a double render cannot count twice.
  const swap = useRef<{ data: unknown; ids: Set<string> | null; n: number }>({ data: null, ids: null, n: 0 });
  if (swap.current.data !== data) {
    const ids = data.map((r: any) => (r && r.id != null ? `${r.id}:${r._isReference ? 'ref' : 'row'}` : null));
    const known = ids.length > 0 && ids.every((x) => x != null);
    const prev = swap.current.ids;
    if (known && prev && prev.size > 0 && ids.filter((x) => !prev.has(x as string)).length * 2 > ids.length) swap.current.n += 1;
    swap.current.ids = known ? new Set(ids as string[]) : null;
    swap.current.data = data;
  }
  useRowsLand(landRef, !loading && !loadFailed && data.length > 0 && !useMobileCards ? `land-${swap.current.n}` : null);

  // The single way the tint is ever removed. Used by every content-shift trigger
  // (scroll, wheel, resize, reload, leave, blur, hidden). Cheap and idempotent.
  const clearHover = useCallback(() => setHoverIdx(null), []);

  // The ONLY setter of a non-null hover. The row is read from the event's own
  // target, so it is always the row physically under the pointer at the instant
  // the event fired — it can never be a replayed/stale coordinate. Do not change
  // this to hit-test a stored point (see the file header for why that recurs).
  const onRowPointer = useCallback((e: React.PointerEvent) => {
    const tr = (e.target as HTMLElement).closest('tr[data-row-index]') as HTMLElement | null;
    const idx = tr ? Number(tr.dataset.rowIndex) : NaN;
    setHoverIdx(Number.isNaN(idx) ? null : idx);
  }, []);

  // Callback ref on the <table>: (dis)connect a ResizeObserver exactly when the
  // table mounts/unmounts. A plain useEffect can't do this reliably because the
  // table first renders as a loading skeleton — by the time the real table
  // mounts, the effect has already run with a null ref. A height change means
  // rows moved under the cursor (rows added/removed, reflow, window resize) —
  // CLEAR the tint; the next real pointer move re-establishes it on the true row.
  const resizeTableRef = resize.tableElRef; // a stable ref object
  // [SEARCH-HIGHLIGHT 2026-09-29, user] Mark the searched text in the rows. `data` + `loading` re-attach
  // it when the real table replaces the skeleton (the ref is null while the skeleton shows).
  useSearchHighlight(resizeTableRef, highlight, loading ? null : data);
  const setTableNode = useCallback((node: HTMLTableElement | null) => {
    resizeTableRef.current = node; // [COL-RESIZE 2026-09-29] a drag writes its width variable here
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (node && typeof ResizeObserver !== 'undefined') {
      roRef.current = new ResizeObserver(clearHover);
      roRef.current.observe(node);
    }
  }, [clearHover, resizeTableRef]);

  // Content shifted under the cursor, or the cursor/attention left — clear the
  // tint outright. None of these can ever PAINT a row; only onRowPointer can.
  // `capture: true` on scroll catches scrolling inside any inner container (the
  // page card, the table's own wrapper), since scroll doesn't bubble.
  useEffect(() => {
    const onVisibility = () => { if (document.hidden) clearHover(); };
    window.addEventListener('scroll', clearHover, { passive: true, capture: true });
    window.addEventListener('wheel', clearHover, { passive: true });
    window.addEventListener('blur', clearHover);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('scroll', clearHover, true);
      window.removeEventListener('wheel', clearHover);
      window.removeEventListener('blur', clearHover);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clearHover]);

  // Every reload hands us a fresh `data` array (silent 60s poll, 5-min refresh,
  // realtime DB push). Clear pre-paint so a reorder/insert/remove can neither
  // leave the tint on the row that used to be there nor paint a phantom on a new
  // one. The highlight returns on the next pointer move over the table.
  useLayoutEffect(() => { clearHover(); }, [data, clearHover]);

  const getSortIcon = (key: string) => {
    if (sort.sortBy !== key) return <i className="fas fa-sort sort-icon"></i>;
    return sort.sortOrder === 'asc' ? (
      <i className="fas fa-sort-up sort-icon active"></i>
    ) : (
      <i className="fas fa-sort-down sort-icon active"></i>
    );
  };

  if (loading) {
    // Skeleton mirrors the real table's columns so it fills in without a jump.
    // [MOBILE 2026-07-07] Card pages get card-shaped skeletons instead.
    return useMobileCards
      ? <MobileRecordList loading columns={columns} data={[]} rowBgFn={rowBgFn} mobileCard={mobileCard} />
      : <TableSkeleton columns={columns} />;
  }

  // [LOAD-STATES 2026-09-29, user] Failed beats empty: an empty list after a failed load is not "no data".
  if (loadFailed) return <LoadFailed what={loadFailed.what} onRetry={loadFailed.onRetry} />;

  if (!data.length) {
    return <EmptyState {...(empty || { title: 'No records found' })} />;
  }

  // [MOBILE 2026-07-07] Below the mobile breakpoint, render the SAME rows/columns/
  // renderers as tappable cards + a full-screen detail instead of a wide table.
  // Desktop (the return below) is untouched.
  if (useMobileCards) {
    return <MobileRecordList columns={columns} data={data} rowBgFn={rowBgFn} mobileCard={mobileCard} />;
  }

  return (
    <TableWrapper ref={landRef} $parentScroll={parentScroll}>
      <Table ref={setTableNode} $fit={fitWidth} $fitMin={fitMinWidth} style={resize.tableStyle}>
        <Thead>
          <tr>
            {columns.map((col) => {
              // [COL-RESIZE 2026-09-29] A dragged column's header takes its width from the variable.
              const dragged = !!resize.cut?.[col.key];
              return (
                <Th
                  key={col.key}
                  $sortable={col.sortable}
                  $align={col.headerAlign ?? col.align}
                  $fit={fitWidth}
                  $nowrap={col.headerNoWrap}
                  $resizable={resize.active}
                  style={resize.thStyle(col.key)}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  <HeaderInner $align={col.headerAlign ?? col.align} style={resize.labelStyle(col.key)}>
                    {dragged ? <CutLabel data-dt-cut="">{col.label}</CutLabel> : <span>{col.label}</span>}
                    {col.sortable && getSortIcon(col.key)}
                  </HeaderInner>
                  {resize.active && <ResizeHandle {...resize.handleProps(col.key, col.label)} />}
                </Th>
              );
            })}
          </tr>
        </Thead>
        {/* Hover is driven purely off these live pointer events (set on move,
            clear on leave). Nothing else paints a row — see the file header. */}
        <tbody ref={flipBodyRef} onPointerMove={onRowPointer} onPointerLeave={clearHover}>
          {data.map((row, i) => (
            <Row
              key={row.id ? `${row.id}-${row._isReference ? 'ref' : 'row'}-${i}` : i}
              row={row}
              columns={columns}
              bg={rowBgFn?.(row)}
              flash={rowFlashFn?.(row) || false}
              hovered={i === hoverIdx}
              fit={fitWidth}
              rowIndex={i}
              cut={resize.cut}
            />
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

// Memoized: DataTable is purely presentational (reads only its props), so
// skipping re-renders when props are shallow-equal can never produce stale UI —
// it just avoids re-rendering hundreds of rows when the parent re-renders for an
// unrelated reason (e.g. an inline-edit keystroke held in a sibling component).
// For this to actually skip work, callers should pass stable `columns`, `onSort`,
// and `rowBgFn` references (see PaymentsPage's useMemo/useCallback).
export default React.memo(DataTable);
